/**
 * ENG-5832 — Host-side agent-session liveness probe.
 *
 * This is the framework-agnostic, READ-ONLY primitive that answers "is the
 * agent's interactive Claude session actually alive on this host right now?"
 * by inspecting the local tmux server and process table. It is deliberately
 * separate from `../liveness/agent-liveness.ts` (ENG-4862), which derives a
 * UI-facing liveness state from heartbeat timestamps reported to the API —
 * that one is pure and browser-safe; THIS one shells out to `tmux`/`pgrep`
 * and is therefore node-only (exposed via the `@augmented/core/runtime/...`
 * subpath, never re-exported from the package barrel).
 *
 * Two consumers share this code so the subtle pgrep matching (ERE anchoring
 * + the `--` option-terminator guard CodeRabbit flagged) lives in one place:
 *   - apps/cli `persistent-session.ts` — the manager's zombie detector, which
 *     wraps these primitives in stateful bookkeeping (it also *kills* a dead
 *     tmux shell; that mutation stays in the CLI).
 *   - packages/mcp channel servers — to decide whether a freshly-arrived
 *     inbound can actually be answered before acking it (see ack-reaction.ts).
 *
 * Tri-state by design. `execFileSync` throws both when a tool reports "no
 * match" (a real negative) AND when the tool is missing or times out (we
 * simply don't know). Collapsing those to a boolean is what makes a probe
 * dangerous to reuse: a host without `tmux`/`pgrep` would look uniformly
 * "dead" and every caller would over-react. We return 'unknown' for the
 * can't-tell cases so callers fail safe.
 */

import { execFileSync } from 'node:child_process';

/** `alive` = confirmed present, `dead` = confirmed absent, `unknown` = couldn't determine. */
export type ProbeState = 'alive' | 'dead' | 'unknown';

/**
 * tmux session name the manager uses when spawning an agent
 * (see persistent-session.ts `spawnSession`: `tmux new-session -s agt-<codeName>`).
 */
export function agentTmuxSessionName(codeName: string): string {
  return `agt-${codeName}`;
}

/**
 * Escape a string for safe embedding in a pgrep ERE pattern. tmux session
 * names are ASCII (`agt-<codeName>`) in practice, but `code_name` is external
 * input so we defensively neutralise every ERE metachar.
 */
export function escapePgrepRegex(value: string): string {
  return value.replace(/[.[\]{}()*+?^$|\\]/g, '\\$&');
}

/**
 * Is a Claude process actually running inside the named tmux session?
 *
 * Matches on the `--name <tmuxSession>` argv pair the manager passes to claude
 * at spawn — the same flag `hasMcpChildren()` reuses successfully.
 *
 *  - exit 0 / output  → 'alive'
 *  - exit 1 (no match) → 'dead'
 *  - pgrep missing (ENOENT) / timeout / other → 'unknown'
 */
export function probeClaudeProcessInTmux(tmuxSession: string): ProbeState {
  // pgrep -f treats the pattern as an unanchored ERE against the full command
  // line. Without an end-boundary, `--name agt-foo` would match a claude
  // running as `--name agt-foobar` and we'd report a dead session as alive
  // (CodeRabbit). Anchor on whitespace/EOL either side so only the exact
  // `--name <tmuxSession>` argv pair matches.
  const escapedSession = escapePgrepRegex(tmuxSession);
  const pattern = `(^|[[:space:]])--name ${escapedSession}([[:space:]]|$)`;
  try {
    // `--` ends pgrep's option parsing — the pattern itself begins with
    // `--name`, which would otherwise be parsed as a flag and produce either a
    // "usage" error or a silent no-match (verified on macOS).
    const out = execFileSync('pgrep', ['-f', '--', pattern], {
      encoding: 'utf-8',
      timeout: 3_000,
    }).trim();
    return out.length > 0 ? 'alive' : 'dead';
  } catch (err) {
    // execFileSync throws on a non-zero exit. pgrep exits 1 specifically when
    // nothing matched — that's an authoritative 'dead'. ENOENT (no pgrep) or
    // any other status (timeout-kill, usage error) is genuinely 'unknown'.
    const e = err as NodeJS.ErrnoException & { status?: number | null };
    if (e?.code === 'ENOENT') return 'unknown';
    return e?.status === 1 ? 'dead' : 'unknown';
  }
}

/**
 * Does the named tmux session exist?
 *
 *  - exit 0            → 'alive'
 *  - tmux missing (ENOENT) → 'unknown'
 *  - any other failure → 'dead' (session absent)
 *
 * NOTE on the "no server running" ambiguity: `tmux has-session` also exits
 * non-zero when no tmux server is running at all, which is indistinguishable
 * here from "server up, session gone". Callers that can't otherwise tell
 * whether the agent is even tmux-managed MUST gate on `process.env.TMUX`
 * (present ⇒ a server is definitely running ⇒ non-zero genuinely means the
 * session is gone). See `probeAgentSessionGated`.
 */
export function probeTmuxSession(tmuxSession: string): ProbeState {
  try {
    execFileSync('tmux', ['has-session', '-t', tmuxSession], {
      stdio: 'ignore',
      timeout: 3_000,
    });
    return 'alive';
  } catch (err) {
    const e = err as NodeJS.ErrnoException;
    if (e?.code === 'ENOENT') return 'unknown';
    return 'dead';
  }
}

export interface SessionLiveness {
  tmux: ProbeState;
  claude: ProbeState;
}

/**
 * Combined read-only probe of an agent's interactive session. The claude
 * probe only runs when tmux is 'alive' — if the session shell is gone the
 * process question is moot, so claude inherits the tmux verdict.
 */
export function probeAgentSession(codeName: string): SessionLiveness {
  const session = agentTmuxSessionName(codeName);
  const tmux = probeTmuxSession(session);
  const claude = tmux === 'alive' ? probeClaudeProcessInTmux(session) : tmux;
  return { tmux, claude };
}

interface ProbeCacheEntry {
  at: number;
  value: SessionLiveness;
}
const probeCache = new Map<string, ProbeCacheEntry>();

/** Default TTL — keeps the pgrep/tmux calls to roughly one per this window per agent. */
export const SESSION_PROBE_TTL_MS = 15_000;

/**
 * `probeAgentSession` with a short TTL cache so a burst of inbound messages
 * doesn't fork a `tmux`/`pgrep` pair per message. The CLI manager uses its own
 * 30s cache; channel servers see higher inbound rates, so the shorter default
 * here trades a little freshness for staying well inside the detection target.
 *
 * @param now injectable clock for tests.
 */
export function probeAgentSessionCached(
  codeName: string,
  ttlMs: number = SESSION_PROBE_TTL_MS,
  now: number = Date.now(),
): SessionLiveness {
  const cached = probeCache.get(codeName);
  if (cached && now - cached.at < ttlMs) return cached.value;
  const value = probeAgentSession(codeName);
  probeCache.set(codeName, { at: now, value });
  return value;
}

/** Test seam: drop the probe cache so each case starts clean. */
export function __resetSessionProbeCache(): void {
  probeCache.clear();
}
